import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Topbar from "../components/Topbar";
import FilterBar from "../components/controls/FilterBar";
import SearchInput from "../components/controls/SearchInput";
import SelectField from "../components/controls/SelectField";
import { listCompanies } from "../api/companies";
import {
  approveCompetitor,
  deleteCompetitor,
  listCompetitors,
} from "../api/competitors";
import { listAdminHotels } from "../api/hotels";
import { CompetitorOut } from "../types/competitors";
import { SCompanyRow } from "../types/companies";
import {
  Building2,
  BedDouble,
  ShieldCheck,
  ShieldAlert,
  CircleCheck,
  CalendarDays,
  LayoutGrid,
  Pencil,
  Table,
  Trash2,
  Loader2,
  ChevronsUpDown,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import AddCompetitorModal from "../components/modals/AddCompetitorModal";
import EditCompetitorModal from "../components/modals/EditCompetitorModal";
import ConfirmDeleteModal from "../components/modals/ConfirmDeleteModal";
import { useDebouncedUrlQuery } from "../hooks/useDebouncedUrlQuery";

const ORDER_OPTIONS = [
  { label: "ID", value: "id" },
  { label: "Компания", value: "company_name" },
  { label: "Дата добавления", value: "created_at" },
] as const;

type OrderField = typeof ORDER_OPTIONS[number]["value"];

type FiltersState = {
  company_id: number | null;
  company_hotel_id: number | null;
  approved: boolean | null;
  order_by: OrderField;
  order_dir: "asc" | "desc";
};

function toErrorMessage(error: any, fallback: string) {
  const detail = error?.response?.data?.detail;

  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    const items = detail
      .map((item) => {
        if (!item) return "";
        if (typeof item === "string") return item;
        if (typeof item === "object") {
          const loc =
            Array.isArray(item.loc) && item.loc.length
              ? `(${item.loc.join(" >")}) `
              : "";
          if (typeof item.msg === "string") return `${loc}${item.msg}`;
          try {
            return `${loc}${JSON.stringify(item)}`;
          } catch {
            return loc.trim();
          }
        }
        return String(item);
      })
      .filter(Boolean);
    if (items.length) return items.join("; ");
  }

  if (detail && typeof detail === "object") {
    if (typeof detail.msg === "string") {
      const loc =
        Array.isArray(detail.loc) && detail.loc.length
          ? `(${detail.loc.join(" >")}) `
          : "";
      return `${loc}${detail.msg}`;
    }
    try {
      return JSON.stringify(detail);
    } catch {
      /* noop */
    }
  }

  if (error?.message) return error.message;

  return fallback;
}

export default function CompetitorsPage() {
  const [viewMode, setViewMode] = useState<"cards" | "table">(() => {
    if (typeof window === "undefined") return "cards";
    const saved = window.localStorage.getItem("competitors:view");
    return saved === "table" ? "table" : "cards";
  });
  const [items, setItems] = useState<CompetitorOut[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [searchDraft, setSearchDraft, searchQuery] = useDebouncedUrlQuery();
  const requestIdRef = useRef(0);

  const [filters, setFilters] = useState<FiltersState>({
    company_id: null,
    company_hotel_id: null,
    approved: null,
    order_by: "id",
    order_dir: "desc",
  });
  const [companies, setCompanies] = useState<SCompanyRow[]>([]);
  const [hotels, setHotels] = useState<Array<{ id: number; name: string; company_name?: string }>>([]);

  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editingCompetitor, setEditingCompetitor] = useState<CompetitorOut | null>(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [deletingCompetitor, setDeletingCompetitor] = useState<CompetitorOut | null>(null);
  const [actionLoadingId, setActionLoadingId] = useState<number | null>(null);
  const [actionType, setActionType] = useState<"approve" | "delete" | null>(null);

  const fetchData = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError("");
    try {
      const params: Record<string, any> = {
        page,
        per_page: perPage,
        q: searchQuery || null,
        order_by: filters.order_by,
        order_dir: filters.order_dir,
      };
      if (filters.company_id !== null) params.company_id = filters.company_id;
      if (filters.company_hotel_id !== null) params.company_hotel_id = filters.company_hotel_id;
      if (filters.approved !== null) params.approved = filters.approved;

      const data = await listCompetitors(params);
      if (requestId !== requestIdRef.current) return;
      setItems(data.items);
      setTotal(data.total);
    } catch (e: any) {
      if (requestId !== requestIdRef.current) return;
      setError(toErrorMessage(e, "Не удалось загрузить конкурентов"));
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [page, perPage, filters, searchQuery]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const data = await listCompanies({ page: 1, per_page: 200 });
        if (!cancelled) {
          setCompanies(data.items);
        }
      } catch (e) {
        console.error("Не удалось загрузить список компаний для фильтра конкурентов", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const data = await listAdminHotels({
          page: 1,
          per_page: 200,
          company_id: filters.company_id,
        });

        if (!cancelled) {
          setHotels(data.items);
        }
      } catch (e) {
        if (!cancelled) {
          setHotels([]);
        }
        console.error("Не удалось загрузить список объектов для фильтра конкурентов", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [filters.company_id]);

  useEffect(() => {
    window.localStorage.setItem("competitors:view", viewMode);
  }, [viewMode]);


  const companyOptions = useMemo(
    () =>
      [...companies]
        .sort((a, b) => a.company_name.localeCompare(b.company_name))
        .map((company) => ({ label: company.company_name, value: String(company.id) })),
    [companies],
  );

  const hotelOptions = useMemo(
    () =>
      [...hotels]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((hotel) => ({
          label: hotel.company_name ? `${hotel.name} (${hotel.company_name})` : hotel.name,
          value: String(hotel.id),
        })),
    [hotels],
  );

  const pages = Math.max(1, Math.ceil(total / perPage));

  const approvedCount = useMemo(
    () => items.filter((item) => item.approved).length,
    [items],
  );

  const pendingCount = useMemo(
    () => items.filter((item) => !item.approved).length,
    [items],
  );

  const uniqueHotelsCount = useMemo(() => {
    const ids = new Set<number>();
    items.forEach((item) => {
      if (item.company_hotel_id) ids.add(item.company_hotel_id);
    });
    return ids.size;
  }, [items]);

  const resolveCompanyId = useCallback(
    (item: CompetitorOut | null) => {
      if (!item) return null;
      if (typeof item.company_id === "number") return item.company_id;
      return filters.company_id;
    },
    [filters.company_id],
  );

  async function handleApprove(item: CompetitorOut, approved = true) {
    setActionLoadingId(item.id);
    setActionType("approve");
    setError("");
    try {
      await approveCompetitor(item.id, approved);
      await fetchData();
    } catch (e: any) {
      setError(toErrorMessage(e, "Не удалось изменить статус конкурента"));
    } finally {
      setActionLoadingId(null);
      setActionType(null);
    }
  }

  const handleDeleteConfirm = useCallback(async () => {
    if (!deletingCompetitor) {
      const err = new Error("Не удалось определить компанию конкурента");
      setError(err.message);
      throw err;
    }

    setActionLoadingId(deletingCompetitor.id);
    setActionType("delete");
    setError("");
    try {
      await deleteCompetitor(deletingCompetitor.id);
      await fetchData();
    } catch (e: any) {
      setError(toErrorMessage(e, "Не удалось удалить конкурента"));
      throw e;
    } finally {
      setActionLoadingId(null);
      setActionType(null);
    }
  }, [deletingCompetitor, fetchData]);

  const hasActiveFilters = Boolean(filters.company_id || filters.company_hotel_id || filters.approved !== null);

  function resetFilters() {
    setFilters((prev) => ({
      ...prev,
      company_id: null,
      company_hotel_id: null,
      approved: null,
    }));
    setPage(1);
  }

  function toggleOrder(field: OrderField) {
    setFilters((prev) => ({
      ...prev,
      order_by: field,
      order_dir: prev.order_by === field ? (prev.order_dir === "asc" ? "desc" : "asc") : "asc",
    }));
    setPage(1);
  }

  function handleCreatedOrUpdated(targetPage = page) {
    if (targetPage !== page) {
      setPage(targetPage);
    } else {
      fetchData();
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 text-slate-900">
      <Topbar />

      <main className="mx-auto w-full max-w-[1200px] px-6 py-10 space-y-8">
        <section className="relative overflow-hidden rounded-3xl border border-white/70 bg-white/90 p-8 shadow-[0_18px_40px_rgba(15,23,42,0.08)] backdrop-blur">
          <div className="absolute inset-y-0 right-0 w-44 translate-x-10 rounded-full bg-gradient-to-b from-indigo-100 to-purple-100 blur-3xl opacity-50 pointer-events-none" />
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div className="space-y-3">
              <h1 className="text-3xl font-bold text-slate-900">Конкуренты</h1>
              <p className="max-w-2xl text-sm text-slate-500">
                Отслеживайте внешние объекты, контролируйте их статусы и поддерживайте актуальность данных для аналитики.
              </p>
            </div>
            <button
              className="inline-flex items-center gap-2 self-start rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-3 text-sm font-semibold text-white shadow-[0_15px_30px_rgba(99,102,241,0.25)] transition hover:translate-y-[-1px] hover:brightness-105 disabled:opacity-50"
              onClick={() => setOpenAdd(true)}
              disabled={loading}
            >
              <span className="text-lg leading-none">+</span> Добавить конкурента
            </button>
          </div>
        </section>

        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-3xl border border-white/70 bg-white/90 p-5 shadow-[0_10px_30px_rgba(15,23,42,0.08)] backdrop-blur">
            <p className="text-xs uppercase tracking-wide text-slate-400">Всего конкурентов</p>
            <div className="mt-2 text-3xl font-semibold text-slate-900">{total}</div>
            <p className="mt-1 text-sm text-slate-500">С учётом активных фильтров</p>
          </div>
          <div className="rounded-3xl border border-white/70 bg-white/90 p-5 shadow-[0_10px_30px_rgba(15,23,42,0.08)] backdrop-blur">
            <p className="text-xs uppercase tracking-wide text-slate-400">Одобрено</p>
            <div className="mt-2 text-3xl font-semibold text-slate-900">{approvedCount}</div>
            <p className="mt-1 text-sm text-slate-500">На текущей странице выдачи</p>
          </div>
          <div className="rounded-3xl border border-white/70 bg-white/90 p-5 shadow-[0_10px_30px_rgba(15,23,42,0.08)] backdrop-blur">
            <p className="text-xs uppercase tracking-wide text-slate-400">Ожидают проверки</p>
            <div className="mt-2 text-3xl font-semibold text-slate-900">{pendingCount}</div>
            <p className="mt-1 text-sm text-slate-500">Требуют подтверждения</p>
          </div>
          <div className="rounded-3xl border border-white/70 bg-white/90 p-5 shadow-[0_10px_30px_rgba(15,23,42,0.08)] backdrop-blur">
            <p className="text-xs uppercase tracking-wide text-slate-400">Уникальных объектов</p>
            <div className="mt-2 text-3xl font-semibold text-slate-900">{uniqueHotelsCount}</div>
            <p className="mt-1 text-sm text-slate-500">На текущей странице</p>
          </div>
        </section>

        <AddCompetitorModal
          open={openAdd}
          onClose={() => setOpenAdd(false)}
          companies={companies}
          defaultCompanyId={filters.company_id}
          defaultHotelId={filters.company_hotel_id}
          onCreated={() => {
            setOpenAdd(false);
            handleCreatedOrUpdated(1);
          }}
        />

        <EditCompetitorModal
          open={openEdit}
          onClose={() => {
            setOpenEdit(false);
            setEditingCompetitor(null);
          }}
          competitor={editingCompetitor}
          companyId={resolveCompanyId(editingCompetitor)}
          onUpdated={() => {
            setOpenEdit(false);
            setEditingCompetitor(null);
            handleCreatedOrUpdated();
          }}
        />

        <ConfirmDeleteModal
          open={openDelete}
          onClose={() => {
            setOpenDelete(false);
            setDeletingCompetitor(null);
          }}
          onConfirm={handleDeleteConfirm}
          title="Удаление конкурента"
          message={
            deletingCompetitor
              ? `Вы точно хотите удалить конкурента — ${deletingCompetitor.url || deletingCompetitor.name || "—"}?`
              : "Вы уверены, что хотите удалить конкурента?"
          }
        />

        <section className="rounded-3xl border border-white/70 bg-white/90 p-5 shadow-[0_10px_30px_rgba(15,23,42,0.08)] backdrop-blur">
          <FilterBar
            sticky={false}
            className="mb-0"
            onReset={resetFilters}
            hasActive={hasActiveFilters}
          >
            <SelectField
              placeholder="Все компании"
              value={filters.company_id !== null ? String(filters.company_id) : ""}
              onChange={(v: string) => {
                setFilters((f) => ({
                  ...f,
                  company_id: v ? Number(v) : null,
                  company_hotel_id: null,
                }));
                setPage(1);
              }}
              options={companyOptions}
              icon={<Building2 size={18} />}
              className="flex-1 min-w-[200px]"
            />
          <SelectField
            placeholder="Все объекты"
            value={filters.company_hotel_id !== null ? String(filters.company_hotel_id) : ""}
            onChange={(v: string) => {
              setFilters((f) => ({
                ...f,
                company_hotel_id: v ? Number(v) : null,
              }));
              setPage(1);
            }}
            options={hotelOptions}
            icon={<BedDouble size={18} />}
            className="flex-1 min-w-[200px]"
          />
            <SelectField
              placeholder="Все статусы"
              value={filters.approved === null ? "" : filters.approved ? "yes" : "no"}
              onChange={(v: string) => {
                setFilters((f) => ({
                  ...f,
                  approved: v === "" ? null : v === "yes",
                }));
                setPage(1);
              }}
              options={[
                { label: "Да", value: "yes" },
                { label: "Нет", value: "no" },
              ]}
              icon={<ShieldCheck size={18} />}
              className="flex-1 min-w-[180px]"
            />
          </FilterBar>
        </section>

        <section className="grid gap-3 rounded-3xl border border-indigo-100 bg-gradient-to-r from-white via-indigo-50/70 to-white p-4 shadow-[0_12px_30px_rgba(99,102,241,0.08)] md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">Поиск конкурентов</p>
            <SearchInput
              value={searchDraft}
              onChange={(value) => {
                setSearchDraft(value);
                setPage(1);
              }}
              placeholder="Поиск по названию конкурента"
              variant="soft"
              className="mt-2"
            />
          </div>
          <div className="rounded-2xl border border-white/80 bg-white/80 px-4 py-3 text-sm text-slate-500 shadow-sm md:text-right">
            <div className="text-xs uppercase tracking-wide text-slate-400">В выдаче</div>
            <div className="mt-1 text-xl font-semibold text-slate-900">{total.toLocaleString("ru-RU")}</div>
          </div>
        </section>

        <section className="rounded-3xl border border-white/60 bg-white/80 p-4 shadow-sm backdrop-blur flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm text-slate-500">Сортировка:</span>
            {ORDER_OPTIONS.map((option) => {
              const isActive = filters.order_by === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => toggleOrder(option.value)}
                  className={[
                    "inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm transition",
                    isActive
                      ? "border-slate-900 bg-slate-900 text-white shadow-lg shadow-slate-900/20"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
                  ].join(" ")}
                >
                  {option.label}
                  {isActive ? (
                    filters.order_dir === "asc" ? (
                      <ChevronUp size={16} className="text-white" />
                    ) : (
                      <ChevronDown size={16} className="text-white" />
                    )
                  ) : (
                    <ChevronsUpDown size={16} className="text-slate-400" />
                  )}
                </button>
              );
            })}
          </div>
          <div className="inline-flex items-center rounded-2xl border border-slate-200 bg-white p-1 shadow-sm">
            <button
              type="button"
              onClick={() => setViewMode("cards")}
              className={`inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold transition ${
                viewMode === "cards" ? "bg-indigo-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              <LayoutGrid size={16} />
              Плашки
            </button>
            <button
              type="button"
              onClick={() => setViewMode("table")}
              className={`inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold transition ${
                viewMode === "table" ? "bg-indigo-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              <Table size={16} />
              Таблица
            </button>
          </div>
        </section>

        {error && (
          <div className="rounded-3xl border border-amber-200 bg-amber-50/90 px-5 py-4 text-sm text-amber-800 shadow-inner">
            {typeof error === "string" ? error : JSON.stringify(error)}
          </div>
        )}

        {loading && (
          <div className="rounded-3xl border border-white/70 bg-white/90 px-6 py-12 text-center text-slate-500 shadow-[0_12px_30px_rgba(15,23,42,0.08)] backdrop-blur">
            <div className="inline-flex items-center gap-2 text-sm">
              <Loader2 className="animate-spin text-indigo-600" size={18} />
              Загрузка конкурентов…
            </div>
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="rounded-3xl border border-white/70 bg-white/90 px-6 py-12 text-center text-slate-500 shadow-[0_12px_30px_rgba(15,23,42,0.08)] backdrop-blur">
            {searchQuery ? "Ничего не найдено" : "Конкуренты не найдены"}
          </div>
        )}

        {!loading && items.length > 0 && viewMode === "cards" && (
          <div className="space-y-4">
            {items.map((item) => {
              const isApprovePending = actionLoadingId === item.id && actionType === "approve";
              const isDeletePending = actionLoadingId === item.id && actionType === "delete";

              return (
                <div
                  key={item.id}
                  className="rounded-3xl border border-white/70 bg-white/95 p-6 shadow-[0_20px_45px_rgba(15,23,42,0.1)] transition hover:-translate-y-[1px] hover:shadow-[0_25px_55px_rgba(15,23,42,0.12)]"
                >
                  <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,2.6fr)_minmax(0,2fr)_minmax(0,1.6fr)] lg:items-start">
                    <div>
                      <div className="text-xs uppercase tracking-wide text-slate-400">Конкурент</div>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <span className="text-lg font-semibold text-slate-900">{item.name || "—"}</span>
                        <span className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-500">
                          ID: {item.id}
                        </span>
                      </div>
                      <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-slate-500">
                        {item.platform && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-slate-600">
                            <ShieldCheck size={14} className="text-indigo-500" />
                            {item.platform}
                          </span>
                        )}
                        <a
                          href={item.url || "#"}
                          target={item.url ? "_blank" : undefined}
                          rel={item.url ? "noreferrer" : undefined}
                          className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-3 py-1 text-indigo-700 transition hover:bg-indigo-100"
                        >
                          <CircleCheck size={14} />
                          <span className="max-w-[220px] truncate">{item.url || "—"}</span>
                        </a>
                      </div>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
                        <p className="text-xs uppercase tracking-wide text-slate-400">Объект</p>
                        <div className="mt-2 flex items-center gap-2 text-sm font-semibold text-slate-800">
                          <BedDouble size={16} className="text-slate-500" />
                          {item.company_hotel_name || item.hotel_name || "—"}
                        </div>
                        {item.address && <p className="mt-2 text-xs text-slate-500">{item.address}</p>}
                      </div>
                      <div className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
                        <p className="text-xs uppercase tracking-wide text-slate-400">Компания</p>
                        <div className="mt-2 flex items-center gap-2 text-sm font-semibold text-slate-800">
                          <Building2 size={16} className="text-slate-500" />
                          {item.company_name || "—"}
                        </div>
                        <p className="mt-2 text-xs text-slate-500">ID объекта: {item.company_hotel_id}</p>
                      </div>
                    </div>

                    <div className="flex flex-col gap-3 text-sm text-slate-500 lg:items-end">
                      <div
                        className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium ${
                          item.approved
                            ? "bg-green-100 text-green-700"
                            : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {item.approved ? <ShieldCheck size={16} /> : <ShieldAlert size={16} />}
                        {item.approved ? "Одобрено" : "Не одобрено"}
                      </div>
                      <div className="inline-flex items-center gap-2">
                        <CalendarDays size={16} className="text-slate-400" />
                        {item.created_at ? new Date(item.created_at).toLocaleDateString("ru-RU") : "—"}
                      </div>
                      {item.approved_at && (
                        <div className="inline-flex items-center gap-2">
                          <CalendarDays size={16} className="text-slate-400" />
                          Одобрено: {new Date(item.approved_at).toLocaleDateString("ru-RU")}
                        </div>
                      )}
                      <div className="flex flex-wrap gap-2 pt-1">
                        <button
                          className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white p-2 text-slate-600 transition hover:bg-slate-50"
                          onClick={() => {
                            setEditingCompetitor(item);
                            setOpenEdit(true);
                          }}
                          type="button"
                        >
                          <Pencil size={18} />
                        </button>
                        <button
                          className="inline-flex items-center justify-center rounded-2xl border border-red-200 bg-red-50 p-2 text-red-600 transition hover:bg-red-100 disabled:opacity-50"
                          onClick={() => {
                            setDeletingCompetitor(item);
                            setOpenDelete(true);
                          }}
                          type="button"
                          disabled={isDeletePending}
                        >
                          {isDeletePending ? <Loader2 size={18} className="animate-spin" /> : <Trash2 size={18} />}
                        </button>
                        {!item.approved && (
                          <button
                            className="inline-flex items-center justify-center rounded-2xl bg-gradient-to-r from-emerald-500 to-emerald-600 p-2 text-white shadow-[0_12px_24px_rgba(16,185,129,0.35)] transition hover:translate-y-[-1px] disabled:opacity-50"
                            onClick={() => handleApprove(item, true)}
                            type="button"
                            disabled={isApprovePending}
                          >
                            {isApprovePending ? <Loader2 size={18} className="animate-spin" /> : <CircleCheck size={18} />}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && items.length > 0 && viewMode === "table" && (
          <div className="overflow-hidden rounded-3xl border border-white/70 bg-white/95 shadow-[0_20px_45px_rgba(15,23,42,0.1)]">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm text-slate-600">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-400">
                  <tr>
                    <th className="px-5 py-4">Конкурент</th>
                    <th className="px-5 py-4">Объект</th>
                    <th className="px-5 py-4">Компания</th>
                    <th className="px-5 py-4">Статус</th>
                    <th className="px-5 py-4">Ссылка</th>
                    <th className="px-5 py-4">Даты</th>
                    <th className="px-5 py-4 text-right">Действия</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.map((item) => {
                    const isApprovePending = actionLoadingId === item.id && actionType === "approve";
                    const isDeletePending = actionLoadingId === item.id && actionType === "delete";
                    return (
                      <tr key={item.id} className="transition hover:bg-slate-50/70">
                        <td className="px-5 py-4">
                          <div className="font-semibold text-slate-900">{item.name || "—"}</div>
                          <div className="text-xs uppercase tracking-wide text-slate-400">ID {item.id}</div>
                          {item.platform && (
                            <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600">
                              <ShieldCheck size={12} className="text-indigo-500" />
                              {item.platform}
                            </div>
                          )}
                        </td>
                        <td className="px-5 py-4">
                          <div className="inline-flex items-center gap-2 text-sm text-slate-700">
                            <BedDouble size={16} className="text-slate-500" />
                            {item.company_hotel_name || item.hotel_name || "—"}
                          </div>
                          <div className="mt-1 text-xs text-slate-400">ID объекта: {item.company_hotel_id ?? "—"}</div>
                        </td>
                        <td className="px-5 py-4">
                          <div className="inline-flex items-center gap-2 text-sm text-slate-700">
                            <Building2 size={16} className="text-slate-500" />
                            {item.company_name || "—"}
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <div
                            className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium ${
                              item.approved ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
                            }`}
                          >
                            {item.approved ? <ShieldCheck size={14} /> : <ShieldAlert size={14} />}
                            {item.approved ? "Одобрено" : "Не одобрено"}
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          {item.url ? (
                            <a
                              href={item.url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700"
                            >
                              <CircleCheck size={14} />
                              <span className="max-w-[200px] truncate">{item.url}</span>
                            </a>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="px-5 py-4">
                          <div className="inline-flex items-center gap-2 text-sm text-slate-700">
                            <CalendarDays size={16} className="text-slate-400" />
                            {item.created_at ? new Date(item.created_at).toLocaleDateString("ru-RU") : "—"}
                          </div>
                          {item.approved_at && (
                            <div className="mt-1 text-xs text-slate-500">
                              Одобрено: {new Date(item.approved_at).toLocaleDateString("ru-RU")}
                            </div>
                          )}
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex justify-end gap-2">
                            <button
                              className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white p-2 text-slate-600 transition hover:bg-slate-50"
                              onClick={() => {
                                setEditingCompetitor(item);
                                setOpenEdit(true);
                              }}
                              type="button"
                            >
                              <Pencil size={18} />
                            </button>
                            <button
                              className="inline-flex items-center justify-center rounded-2xl border border-red-200 bg-red-50 p-2 text-red-600 transition hover:bg-red-100 disabled:opacity-50"
                              onClick={() => {
                                setDeletingCompetitor(item);
                                setOpenDelete(true);
                              }}
                              type="button"
                              disabled={isDeletePending}
                            >
                              {isDeletePending ? <Loader2 size={18} className="animate-spin" /> : <Trash2 size={18} />}
                            </button>
                            {!item.approved && (
                              <button
                                className="inline-flex items-center justify-center rounded-2xl bg-gradient-to-r from-emerald-500 to-emerald-600 p-2 text-white shadow-[0_12px_24px_rgba(16,185,129,0.35)] transition hover:translate-y-[-1px] disabled:opacity-50"
                                onClick={() => handleApprove(item, true)}
                                type="button"
                                disabled={isApprovePending}
                              >
                                {isApprovePending ? <Loader2 size={18} className="animate-spin" /> : <CircleCheck size={18} />}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <section className="flex flex-wrap items-center gap-4 rounded-3xl border border-white/70 bg-white/90 p-5 shadow-[0_10px_30px_rgba(15,23,42,0.08)] backdrop-blur">
          <div className="flex items-center gap-2">
            <label className="text-sm text-slate-600">На странице:</label>
            <select
              value={perPage}
              onChange={(e) => {
                const next = Number(e.target.value);
                setPerPage(next);
                setPage(1);
              }}
              className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-inner"
            >
              {[10, 20, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2 sm:ml-auto">
            <button
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
            >
              Назад
            </button>
            <div className="rounded-2xl bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-800">
              {page} / {pages}
            </div>
            <button
              disabled={page >= pages || loading}
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
              className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
            >
              Вперёд
            </button>
          </div>
        </section>
      </main>
    </div>
  );
}
